import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Consultation } from "../../../services/profile/profileModel";
import { User } from "../../../services/user/userModel";
import { userService } from "../../../services/user/userService";
import { cx } from "../../../react-helpers/css";
import { loggerBuilder } from "../../../services/logger";
import { toastsWithIntl } from "../../../services/toastService";
import CandidateIcon from "../CandidateIcon";

const logger = loggerBuilder("premium-stats");

const DAY_MS = 24 * 3600 * 1000;
// Lignes visibles avant « Voir plus » : l'historique complet reste accessible, la fiche ne s'allonge pas.
const HISTORY_SHOWN = 6;
// Blurred teaser values shown to non-premium candidates
const DUMMY_TOTAL_VIEWS = 47;
const DUMMY_CONSULTATIONS: Consultation[] = [
  {
    consultationDate: new Date(Date.now() - 2 * DAY_MS).toISOString(),
    organizationName: "VEOLIA",
  },
  {
    consultationDate: new Date(Date.now() - 4 * DAY_MS).toISOString(),
    organizationName: "SNCF",
  },
  {
    consultationDate: new Date(Date.now() - 9 * DAY_MS).toISOString(),
    organizationName: "CARREFOUR",
  },
];

const PremiumStatsSection = ({
  loggedUser,
  isPremium,
  consultations,
}: {
  loggedUser: User;
  isPremium: boolean;
  consultations: Consultation[];
}) => {
  const { t } = useTranslation(["candidate"]);
  const { getPremiumUserLink } = userService();
  const { toastError } = toastsWithIntl(["common"]);
  const [loadingPremium, setLoadingPremium] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const history = isPremium
    ? consultations.filter((consultation) => !!consultation.organizationName)
    : DUMMY_CONSULTATIONS;
  const totalViews = isPremium ? consultations.length : DUMMY_TOTAL_VIEWS;
  const hiddenCount = Math.max(0, history.length - HISTORY_SHOWN);
  const visibleHistory = showAll ? history : history.slice(0, HISTORY_SHOWN);

  return (
    <section className="ui-card --pad cand-stats">
      <div className="cand-stats_head">
        <h2 className="ui-st cand-stats_title">
          {t("candidate:profile.premium-stats.TITLE")}
        </h2>
        {!isPremium && (
          <span className="ui-chip --prem">
            {t("candidate:profile.premium-stats.BADGE")}
          </span>
        )}
      </div>
      <div className="cand-stats_body">
        <div className="ui-kpi cand-stats_kpi">
          <span className="ui-kpi_l">
            <CandidateIcon name="eye" />
            {t("candidate:profile.premium-stats.TOTAL_VIEWS_LABEL")}
          </span>
          <b className={cx(["ui-kpi_v", !isPremium && "--blurred"])}>
            {totalViews}
          </b>
        </div>
        {history.length > 0 && (
          <div>
            <p className="cand-stats_label">
              {t("candidate:profile.premium-stats.HISTORY_LABEL")}
              {isPremium && <span className="ui-num"> ({history.length})</span>}
            </p>
            <ul id="cand-stats-history" className="cand-stats_history">
              {visibleHistory.map((consultation, index) => (
                <li key={index} className={cx([!isPremium && "--blurred"])}>
                  <b>{consultation.organizationName}</b>
                  <span className="ui-muted ui-small ui-num">
                    {t("candidate:profile.premium-stats.HISTORY_DATE", {
                      date: new Date(consultation.consultationDate),
                    })}
                  </span>
                </li>
              ))}
            </ul>
            {hiddenCount > 0 && (
              <button
                type="button"
                className="btn--soft --btn--s cand-stats_more"
                aria-expanded={showAll}
                aria-controls="cand-stats-history"
                onClick={() => setShowAll((v) => !v)}
              >
                {showAll
                  ? t("candidate:profile.premium-stats.SHOW_LESS")
                  : t("candidate:profile.premium-stats.SHOW_MORE", {
                      n: hiddenCount,
                    })}
              </button>
            )}
          </div>
        )}
      </div>
      {!isPremium && (
        <div className="cand-stats_cta">
          <button
            className="btn--1"
            disabled={loadingPremium}
            onClick={() => {
              const handlePremiumClick = async () => {
                try {
                  setLoadingPremium(true);
                  const result = await getPremiumUserLink(loggedUser.id);
                  if (result?.url) window.location.href = result.url;
                } catch (err) {
                  logger.error(
                    "Erreur lors de la récupération du lien premium",
                    err,
                  );
                  toastError("common:error.header.PREMIUM_LINK_ERROR");
                  return;
                } finally {
                  setLoadingPremium(false);
                }
              };

              void handlePremiumClick();
            }}
          >
            {loadingPremium ? (
              "..."
            ) : (
              <>
                <CandidateIcon name="bolt" />
                {t("candidate:profile.premium-stats.CTA")}
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};

export default PremiumStatsSection;
